"use client";

/**
 * Chart visibility resync gate (proposal 65).
 *
 * The live-candle pump (Chart.tsx `flushLiveCandleImpl`) and the OHLC batch
 * queue (useChartStore3 `processBatchQueue`) both ride requestAnimationFrame,
 * which the browser freezes to zero on a hidden tab. On return, the naive
 * new-candle branch would append ONE lone candle at "now", stranding a gap of
 * the bars missed while hidden — the "misrendered chart".
 *
 * This per-panel gate closes that hole:
 *   - on `visibilitychange → hidden` it cancels the live-candle flush, drops the
 *     pending tick, and marks the panel STALE (no phantom candle can be queued
 *     for the wake),
 *   - on `→ visible` (only for on-screen panels) it waits for the socket recycle
 *     (useWakeWatchdog / useConnectionFreshnessStore) to land, then BACKFILLS the
 *     missing [lastGoodBarTs, now] range, splices it deduped + sorted, applies it
 *     to the chart, and only THEN marks the panel LIVE so ticks resume.
 *
 * The invariant it enforces (belt-and-braces with the flush guard in Chart.tsx):
 * a live tick may NEVER advance the series across an un-backfilled gap.
 */

import { useEffect, useRef } from "react";
import type { KLineData } from "klinecharts";
import {
  useChartStore3,
  type ChartBackfillResult,
} from "@/vybe-trader-client/chart/stores/useChartStore3";
import { useConnectionFreshnessStore } from "@/vybe-trader-client/connection/stores/useConnectionFreshnessStore";
import type { ChartPanelId } from "@/vybe-trader-client/chart/stores/chartPanelTypes";
import { getVisibleChartPanelIds } from "@/lib/chart-navigation/getVisibleChartPanelIds";
import { useDesktopLayoutStore } from "@/lib/desktop-layout/store";

/** How long we wait for the connection freshness to return to "live" before backfilling anyway. */
const CONNECTION_RECYCLE_WAIT_MS = 8000;

interface ChartInstanceLike {
  getDataList?: () => KLineData[];
}

export interface ChartVisibilityGateOptions {
  chartPanelId: ChartPanelId;
  /** Ref to the live klinecharts instance (may be null between symbol switches). */
  chartInstanceRef: React.MutableRefObject<ChartInstanceLike | null>;
  /** Freezes the rAF/timeout live-candle pump. */
  cancelLiveCandleFlush: () => void;
  /** Pending live tick that must be dropped on hide so it can't paint on wake. */
  liveCandlePendingRef: React.MutableRefObject<unknown>;
  /**
   * Applies a re-aggregated tail to the chart (Chart.tsx wraps
   * `applyInitialDataIfCurrent` here). Returns whether it actually applied.
   */
  applyResyncedData: (bars: Array<{ timestamp: number }>) => boolean;
}

/** Resolves once connection freshness is "live" again (or after a timeout). */
function waitForConnectionLive(timeoutMs: number): Promise<void> {
  if (useConnectionFreshnessStore.getState().freshness === "live") {
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      unsubscribe();
      clearTimeout(timer);
      resolve();
    };
    const unsubscribe = useConnectionFreshnessStore.subscribe((state) => {
      if (state.freshness === "live") finish();
    });
    const timer = setTimeout(finish, timeoutMs);
  });
}

export function useChartVisibilityGate(
  options: ChartVisibilityGateOptions,
): void {
  const {
    chartPanelId,
    chartInstanceRef,
    cancelLiveCandleFlush,
    liveCandlePendingRef,
    applyResyncedData,
  } = options;

  // Keep the mutable bits in a ref so the visibility listener stays stable and
  // is not re-bound on every render (the pump refs are stable already, but the
  // apply callback is recreated per render).
  const optsRef = useRef(options);
  optsRef.current = options;

  // Guards against overlapping resyncs (double visibilitychange, StrictMode).
  const resyncingRef = useRef(false);

  useEffect(() => {
    function lastBarTs(): number | null {
      const list = chartInstanceRef.current?.getDataList?.();
      if (!list || list.length === 0) return null;
      const ts = list[list.length - 1]?.timestamp;
      return typeof ts === "number" ? ts : null;
    }

    async function resync(): Promise<void> {
      if (resyncingRef.current) return;
      const store = useChartStore3.getState();
      const chart = optsRef.current.chartInstanceRef.current;
      if (!chart) {
        store.markChartLive(chartPanelId);
        return;
      }

      const lastGoodBarTs = lastBarTs();
      if (lastGoodBarTs == null) {
        // Nothing painted yet — the normal init path owns the first load.
        store.markChartLive(chartPanelId);
        return;
      }

      resyncingRef.current = true;
      try {
        // Sequence BEHIND the socket recovery so the two recoveries don't race
        // (useWakeWatchdog recycles the socket + resyncs the quote feed first).
        await waitForConnectionLive(CONNECTION_RECYCLE_WAIT_MS);

        const now = Date.now();
        if (now <= lastGoodBarTs) {
          store.markChartLive(chartPanelId);
          return;
        }

        store.markChartResyncing(chartPanelId);
        const result: ChartBackfillResult = await store.backfillRange(
          chartPanelId,
          lastGoodBarTs,
          now,
        );
        if (result.bars.length > 0) {
          optsRef.current.applyResyncedData(result.bars);
        }
      } finally {
        // Re-arm: marking live lets the flush guard pass, so the next quote
        // tick resumes the pump on a gap-free series.
        useChartStore3.getState().markChartLive(chartPanelId);
        resyncingRef.current = false;
      }
    }

    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        // Freeze the pump and drop the pending tick BEFORE the tab is throttled
        // so no phantom "now" candle can be queued for the wake.
        optsRef.current.cancelLiveCandleFlush();
        optsRef.current.liveCandlePendingRef.current = null;
        useChartStore3.getState().markChartStale(chartPanelId, lastBarTs());
      } else {
        // Only on-screen panels resync now; a hidden split-pane resyncs lazily
        // the moment it is shown (its own mount + this listener re-fire).
        const mode = useDesktopLayoutStore.getState().chartLayoutMode;
        if (!getVisibleChartPanelIds(mode).includes(chartPanelId)) return;
        // Only act if we actually went stale (ignore spurious visible→visible).
        if (
          useChartStore3.getState().slots[chartPanelId].chartFreshness ===
          "live"
        ) {
          return;
        }
        void resync();
      }
    };

    // A panel that was NOT on-screen at wake time is skipped above and stays
    // "stale" — and `visibilitychange` is the only event this listener gets, so
    // it never fires again when the desktop layout later reveals that panel.
    // The result is a permanently gated pump: `flushLiveCandleImpl` drops every
    // new-bucket tick while the ASK/BID overlay (applied before the gate) keeps
    // tracking the quote, i.e. frozen candles next to live bid/ask lines.
    //
    // Re-arm on the two occasions that can reveal a stale panel without a
    // visibilitychange: this gate mounting, and the layout mode changing.
    const rearmIfRevealed = () => {
      if (document.visibilityState !== "visible") return;
      const mode = useDesktopLayoutStore.getState().chartLayoutMode;
      if (!getVisibleChartPanelIds(mode).includes(chartPanelId)) return;
      if (
        useChartStore3.getState().slots[chartPanelId].chartFreshness === "live"
      ) {
        return;
      }
      void resync();
    };

    rearmIfRevealed();
    const unsubscribeLayout = useDesktopLayoutStore.subscribe(rearmIfRevealed);

    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      unsubscribeLayout();
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // chartPanelId is stable for the lifetime of a mounted Chart; the pump refs
    // and apply callback are read through optsRef so they never re-bind here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartPanelId]);
}
